/**
 * AI 分析卡片组件
 */

import React, { ReactNode } from 'react';
import { Card, Button, Typography, Alert } from 'antd';
import { RobotOutlined, LoadingOutlined, SettingOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

const { Paragraph } = Typography;

interface AiAnalysisCardProps {
    aiAnalyzing: boolean;
    aiResult: string | null;
    aiError?: string | null;
    onAnalyze: () => void;
}

// Markdown 组件自定义样式
const markdownComponents = {
    h1: ({ children }: { children?: ReactNode }) => (
        <h2 style={{ fontSize: 16, marginTop: 12, marginBottom: 6, borderBottom: '1px solid #f0f0f0', paddingBottom: 6 }}>
            {children}
        </h2>
    ),
    h2: ({ children }: { children?: ReactNode }) => (
        <h3 style={{ fontSize: 14, marginTop: 10, marginBottom: 4, color: '#1890ff' }}>{children}</h3>
    ),
    h3: ({ children }: { children?: ReactNode }) => (
        <h4 style={{ fontSize: 13, marginTop: 8, marginBottom: 4, color: '#722ed1' }}>{children}</h4>
    ),
    p: ({ children }: { children?: ReactNode }) => (
        <Paragraph style={{ marginBottom: 6, lineHeight: 1.6, fontSize: 13 }}>{children}</Paragraph>
    ),
    ul: ({ children }: { children?: ReactNode }) => (
        <ul style={{ paddingLeft: 16, marginBottom: 6, fontSize: 13 }}>{children}</ul>
    ),
    li: ({ children }: { children?: ReactNode }) => <li style={{ marginBottom: 3 }}>{children}</li>,
    strong: ({ children }: { children?: ReactNode }) => <strong style={{ color: '#fa541c' }}>{children}</strong>,
};

export const AiAnalysisCard: React.FC<AiAnalysisCardProps> = ({ aiAnalyzing, aiResult, aiError, onAnalyze }) => {
    return (
        <Card
            size="small"
            title={
                <span style={{ fontSize: 14 }}>
                    <RobotOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                    AI 深度分析
                </span>
            }
            style={{ height: '100%' }}
            bodyStyle={{
                maxHeight: 'calc(100vh - 400px)',
                overflowY: 'auto',
                padding: '12px 16px'
            }}
            extra={
                <Button
                    type="primary"
                    size="small"
                    icon={aiAnalyzing ? <LoadingOutlined /> : <RobotOutlined />}
                    onClick={onAnalyze}
                    loading={aiAnalyzing}
                    disabled={aiAnalyzing}
                >
                    {aiAnalyzing ? '分析中' : '开始分析'}
                </Button>
            }
        >
            {aiError ? (
                <Alert
                    type="warning"
                    message="分析失败"
                    description={
                        <div>
                            <p style={{ marginBottom: 8 }}>{aiError}</p>
                            {(aiError.includes('服务器') || aiError.includes('Gemini')) && (
                                <Link to="/config">
                                    <Button size="small" icon={<SettingOutlined />}>
                                        检查设置
                                    </Button>
                                </Link>
                            )}
                        </div>
                    }
                    style={{ marginBottom: 12 }}
                />
            ) : null}

            {aiResult ? (
                <div>
                    <ReactMarkdown components={markdownComponents}>{aiResult}</ReactMarkdown>
                </div>
            ) : !aiError ? (
                <div style={{ textAlign: 'center', padding: '24px 12px', color: '#999' }}>
                    <RobotOutlined style={{ fontSize: 28, marginBottom: 10, color: '#d9d9d9' }} />
                    <p style={{ fontSize: 13, marginBottom: 4 }}>点击"开始分析"按钮</p>
                    <p style={{ fontSize: 12 }}>基于竞品数据生成深度分析报告</p>
                </div>
            ) : null}
        </Card>
    );
};

